/**
 * Handle Configuration Constants
 *
 * Centralized configuration for connection handles on FlowScreenNode.
 * Controls sizing, positioning, and styling of input/output handles.
 *
 * Sprint: sprint-4
 * Related: FR-006 to FR-012 (Connection Rules & Validation)
 */

/**
 * Handle dimensions (px)
 *
 * INPUT_WIDTH: Wide enough for "INPUT" text
 * OUTPUT_WIDTH: Wide enough for button labels
 * HEIGHT: Consistent height for all handles
 */
export const HANDLE_DIMENSIONS = {
  INPUT_WIDTH: 56,
  OUTPUT_WIDTH: 80,
  HEIGHT: 24,
} as const;

/**
 * Handle positioning offsets from node boundary (px)
 *
 * INPUT_LEFT: Negative value positions handle outside node left edge
 * OUTPUT_RIGHT: Negative value positions handle outside node right edge
 * OUTPUT_GAP: Gap between node right edge and handle
 * CLOSER_TO_NODE_OFFSET: Additional offset to bring handles closer to node (12px)
 */
export const HANDLE_OFFSETS = {
  CLOSER_TO_NODE_OFFSET: 12, // Bring handles 12px closer to node
  INPUT_LEFT: -44, // Position INPUT handle outside with 12px closer to node (56 - 12 = 44)
  OUTPUT_GAP: -32, // Scaled gap to maintain ~40% overlap ratio with 80px width
  get OUTPUT_RIGHT() {
    // Calculate: -(width + gap)
    return -(HANDLE_DIMENSIONS.OUTPUT_WIDTH + this.OUTPUT_GAP);
  }
} as const;

/**
 * Vertical distribution of multiple output handles (px)
 *
 * FIRST_TOP: Top position of first handle
 * INTERVAL: Vertical spacing between consecutive handles
 */
export const HANDLE_SPACING = {
  FIRST_TOP: 30,
  INTERVAL: 50,
} as const;

/**
 * Positioning regions for semantic handle placement
 *
 * Handles are positioned based on their semantic type (actionKey):
 * - TOP_REGION: For slider.timer handles
 * - MIDDLE_REGION: For list.item.N handles (vertically distributed)
 * - BOTTOM_REGION: For footer.button1 handles
 *
 * Values proportionally scaled for 932px height (unified with SCREEN_DESIGN_SIZE):
 * - TOP_START: 7.1% of 932px = 66px
 * - MIDDLE_START: 23.7% of 932px = 221px
 * - MIDDLE_END: 47.4% of 932px = 442px
 * - BOTTOM_OFFSET: 9.5% of 932px = 89px
 */
export const HANDLE_REGIONS = {
  TOP_START: 66,        // 7.1% of 932px (was 62px at 874px)
  MIDDLE_START: 221,    // 23.7% of 932px (was 207px)
  MIDDLE_END: 442,      // 47.4% of 932px (was 414px)
  BOTTOM_OFFSET: 89,    // 9.5% of 932px (was 83px)
} as const;

/**
 * Handle text content and typography
 *
 * INPUT_LABEL: Text shown in input handle
 * DEFAULT_BUTTON_LABEL: Fallback label for output handles
 * FONT_SIZE: Sizes optimized for small handle dimensions
 */
export const HANDLE_TEXT = {
  INPUT_LABEL: 'INPUT',
  INPUT_FONT_SIZE: '8px',   // Scaled ×1.6 from 5px
  OUTPUT_FONT_SIZE: '10px', // Scaled ×1.6 from 6px
  DEFAULT_BUTTON_PREFIX: 'Button',
  DEFAULT_BTN_PREFIX: 'Btn',
} as const;

/**
 * Handle border radius (px)
 */
export const HANDLE_BORDER_RADIUS = 6;

/**
 * Handle styling configuration
 *
 * Colors: MUI theme palette references
 * Font weights: Typography scale
 * Transitions: Animation timings
 */
export const HANDLE_STYLING = {
  // Default state colors (very light)
  DEFAULT_BACKGROUND: 'grey.50',  // Much lighter background
  DEFAULT_BORDER: 'grey.300',

  // Hover state colors (slightly darker for contrast)
  HOVER_BACKGROUND: 'grey.100',
  HOVER_BORDER: 'grey.400',

  // Connecting state
  CONNECTING_BACKGROUND: '#1976d2', // primary.main
  CONNECTING_SHADOW: '0 0 8px rgba(25, 118, 210, 0.7)',

  // Typography
  FONT_WEIGHT_DEFAULT: 400,
  FONT_WEIGHT_HOVER: 600,

  // Transitions
  TRANSITION_DURATION: '0.2s',
  TRANSITION_TIMING: 'ease',
} as const;

// Type exports for TypeScript inference
export type HandleDimension = typeof HANDLE_DIMENSIONS[keyof typeof HANDLE_DIMENSIONS];
export type HandleOffset = typeof HANDLE_OFFSETS[keyof typeof HANDLE_OFFSETS];
export type HandleStyle = typeof HANDLE_STYLING[keyof typeof HANDLE_STYLING];
